/**
 * What's in the Boxes Section JavaScript
 * Handles tab switching and slider functionality
 */

// Prevent duplicate class declaration
if (typeof WhatsInTheBoxes === 'undefined') {
  class WhatsInTheBoxes {
    constructor() {
      this.sections = document.querySelectorAll('.wt-whats-in-the-boxes');
      this.init();
    }

    init() {
      this.sections.forEach((section) => {
        this.initSection(section);
      });
    }

    initSection(section) {
      const tabs = section.querySelectorAll('.wt-whats-in-the-boxes__tab');
      const sliders = section.querySelectorAll('.wt-whats-in-the-boxes__slider');

      // Handle case where there are no tabs (single content section)
      if (!tabs.length) {
        if (sliders.length > 0) {
          this.initSingleContentSection(section, sliders);
        }
        return;
      }

      // Show first tab's content on load
      const firstTab = tabs[0];
      if (firstTab) {
        this.showFirstTabContent(section, firstTab.dataset.tab);

        // Mark the first tab as active
        firstTab.classList.add('wt-whats-in-the-boxes__tab--active');
      }

      // Initialize tab switching
      tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          this.switchTab(section, tab, tabs, sliders);
        });
      });

      // Initialize all Swiper instances for each slider (if any exist)
      if (sliders.length > 0) {
        sliders.forEach((slider, index) => {
          const swiperContainer = slider.querySelector('[data-swiper]');
          if (swiperContainer) {
            this.initSwiper(section, swiperContainer);

            // Only the first (active) slider should use header navigation initially
            if (index === 0) {
              this.updateSliderNavigation(slider);
            }
          }
        });
      }
    }

    initSingleContentSection(section, sliders) {
      // For sections with no tabs, just show all content and initialize sliders

      // Show all images (since there are no tabs to control visibility)
      const imageContainer = section.querySelector('.wt-whats-in-the-boxes__image');
      if (imageContainer) {
        const allTabImages = imageContainer.querySelectorAll('.wt-whats-in-the-boxes__image-tab');
        allTabImages.forEach((img) => {
          img.style.display = 'block';
          img.style.opacity = '1';
        });
      }

      // Show all overlay content
      const allTabOverlays = section.querySelectorAll('.wt-whats-in-the-boxes__overlay-content-tab');
      allTabOverlays.forEach((overlay) => {
        overlay.style.display = 'flex';
      });

      // Initialize all sliders
      sliders.forEach((slider, index) => {
        const swiperContainer = slider.querySelector('[data-swiper]');
        if (swiperContainer) {
          this.initSwiper(section, swiperContainer);

          // Make all sliders active since there are no tabs
          slider.classList.add('wt-whats-in-the-boxes__slider--active');

          // Update navigation for each slider
          this.updateSliderNavigation(slider);
        }
      });

      // Show all navigation controls
      const allNavControls = section.querySelectorAll('.wt-whats-in-the-boxes__nav-controls-tab');
      allNavControls.forEach((controls) => {
        controls.classList.add('wt-whats-in-the-boxes__nav-controls-tab--active');
      });
    }

    showFirstTabContent(section, firstTabId) {
      // Show first tab's image - use the same logic as updateTabImage for consistency
      const imageContainer = section.querySelector('.wt-whats-in-the-boxes__image');
      if (imageContainer) {
        // Hide all tab images first
        const allTabImages = imageContainer.querySelectorAll('.wt-whats-in-the-boxes__image-tab');
        allTabImages.forEach((img) => {
          img.style.opacity = '0';
          img.style.display = 'block';
        });

        // Show the image for the first tab
        const firstTabImage = imageContainer.querySelector(`[data-tab="${firstTabId}"]`);
        if (firstTabImage) {
          firstTabImage.style.display = 'block';
          // Use a small delay to ensure the opacity transition works
          setTimeout(() => {
            firstTabImage.style.opacity = '1';
          }, 10);
        }
      }

      // Show first tab's overlay content
      const firstTabOverlay = section.querySelector(
        `.wt-whats-in-the-boxes__overlay-content-tab[data-tab="${firstTabId}"]`
      );
      if (firstTabOverlay) {
        firstTabOverlay.style.display = 'flex';
      }
    }

    switchTab(section, activeTab, allTabs, allSliders) {
      const targetTab = activeTab.dataset.tab;

      // Update tab states
      allTabs.forEach((tab) => {
        tab.classList.remove('wt-whats-in-the-boxes__tab--active');
      });
      activeTab.classList.add('wt-whats-in-the-boxes__tab--active');

      // Update image based on active tab
      this.updateTabImage(section, targetTab);

      // Update overlay content based on active tab
      this.updateTabOverlay(section, targetTab);

      // Update slider visibility and navigation
      allSliders.forEach((slider) => {
        slider.classList.remove('wt-whats-in-the-boxes__slider--active');
        if (slider.dataset.tab === targetTab) {
          slider.classList.add('wt-whats-in-the-boxes__slider--active');

          // Ensure the slider is properly initialized before updating navigation
          const swiperContainer = slider.querySelector('[data-swiper]');
          if (swiperContainer && !swiperContainer.swiper) {
            this.initSwiper(section, swiperContainer);
          }

          // Update the active slider's navigation to use header controls
          this.updateSliderNavigation(slider);
        }
      });

      // Update navigation controls visibility
      this.updateNavigationControlsVisibility(section, targetTab);

      // Trigger custom event for other scripts that might need to know about tab changes
      section.dispatchEvent(
        new CustomEvent('whats-in-the-boxes:tab-changed', {
          detail: {
            tab: targetTab,
            activeTab: activeTab,
          },
        })
      );
    }

    updateTabImage(section, targetTab) {
      const imageContainer = section.querySelector('.wt-whats-in-the-boxes__image');
      if (!imageContainer) return;

      // Hide all tab images
      const allTabImages = imageContainer.querySelectorAll('.wt-whats-in-the-boxes__image-tab');
      allTabImages.forEach((img) => {
        img.style.opacity = '0';
        img.style.display = 'block';
      });

      // Show the image for the active tab
      const activeTabImage = imageContainer.querySelector(`[data-tab="${targetTab}"]`);
      if (activeTabImage) {
        activeTabImage.style.display = 'block';
        setTimeout(() => {
          activeTabImage.style.opacity = '1';
        }, 10);
      }
    }

    updateTabOverlay(section, targetTab) {
      // Hide all tab overlay content
      const allTabOverlays = section.querySelectorAll('.wt-whats-in-the-boxes__overlay-content-tab');
      allTabOverlays.forEach((overlay) => {
        overlay.style.display = 'none';
      });

      // Show the overlay content for the active tab
      const activeTabOverlay = section.querySelector(
        `.wt-whats-in-the-boxes__overlay-content-tab[data-tab="${targetTab}"]`
      );
      if (activeTabOverlay) {
        activeTabOverlay.style.display = 'flex';
      }
    }

    initSwiper(section, container) {
      // The theme uses slideshow-section custom element which handles Swiper initialization
      // We just need to ensure the slideshow-section is properly initialized
      const slideshowSection = section.querySelector('slideshow-section');

      if (slideshowSection) {
        // Remove loading class
        container.classList.remove('loading');

        // The slideshow-section custom element will handle Swiper initialization
        // We can access the swiper instance through the custom element
        if (slideshowSection.swiper) {
          container.swiper = slideshowSection.swiper;
        }
      } else {
        container.classList.remove('loading');
      }
    }

    updateSliderNavigation(activeSlider) {
      // Find the swiper instance for the active slider
      const swiperContainer = activeSlider.querySelector('[data-swiper]');
      const slideshowSection = activeSlider;

      if (slideshowSection && slideshowSection.swiper) {
        const swiper = slideshowSection.swiper;
        const targetTab = activeSlider.dataset.tab;

        // Get the navigation buttons for this specific tab
        const navControlsTab = document.querySelector(
          `.wt-whats-in-the-boxes__nav-controls-tab[data-tab="${targetTab}"]`
        );
        const nextBtn = navControlsTab?.querySelector('.wt-whats-in-the-boxes__nav-next');
        const prevBtn = navControlsTab?.querySelector('.wt-whats-in-the-boxes__nav-prev');

        if (nextBtn && prevBtn) {
          // Update navigation elements to point to this tab's controls
          if (swiper.params.navigation) {
            swiper.params.navigation.nextEl = nextBtn;
            swiper.params.navigation.prevEl = prevBtn;
          }

          // Destroy existing navigation if it exists
          if (swiper.navigation) {
            swiper.navigation.destroy();
          }

          // Reinitialize navigation
          if (swiper.navigation) {
            swiper.navigation.init();
            swiper.navigation.update();
          }

          // Force update the swiper to ensure proper state
          setTimeout(() => {
            // Force the swiper to recalculate everything
            if (swiper.update) {
              swiper.update();
            }
            if (swiper.updateSlides) {
              swiper.updateSlides();
            }
            if (swiper.updateSize) {
              swiper.updateSize();
            }
            if (swiper.updateProgress) {
              swiper.updateProgress();
            }
            if (swiper.updateSlidesClasses) {
              swiper.updateSlidesClasses();
            }

            // Force a resize event to recalculate dimensions
            if (swiper.onResize) {
              swiper.onResize();
            }

            // Remove any disabled classes and force update
            nextBtn.classList.remove('swiper-button-disabled');
            prevBtn.classList.remove('swiper-button-disabled');

            if (swiper.navigation) {
              swiper.navigation.update();
            }

            // Force another update after a short delay to ensure everything is calculated
            setTimeout(() => {
              if (swiper.update) {
                swiper.update();
              }
              if (swiper.navigation) {
                swiper.navigation.update();
              }
            }, 100);
          }, 50);
        }

        // Update scrollbar elements
        if (swiper.params.scrollbar) {
          const scrollbarEl = activeSlider.querySelector('.wt-whats-in-the-boxes__scrollbar');
          if (scrollbarEl) {
            swiper.params.scrollbar.el = scrollbarEl;

            // Reinitialize scrollbar
            if (swiper.scrollbar) {
              swiper.scrollbar.init();
              swiper.scrollbar.updateSize();
              swiper.scrollbar.setTranslate();
            }
          }
        }
      }
    }

    updateNavigationControlsVisibility(section, targetTab) {
      // Hide all navigation control tabs
      const allNavControls = section.querySelectorAll('.wt-whats-in-the-boxes__nav-controls-tab');
      allNavControls.forEach((controls) => {
        controls.classList.remove('wt-whats-in-the-boxes__nav-controls-tab--active');
      });

      // Show the navigation controls for the active tab
      const activeNavControls = section.querySelector(
        `.wt-whats-in-the-boxes__nav-controls-tab[data-tab="${targetTab}"]`
      );
      if (activeNavControls) {
        activeNavControls.classList.add('wt-whats-in-the-boxes__nav-controls-tab--active');
      }
    }

    handleSlideChange(section, swiper) {
      // Custom logic for slide changes can be added here
      // For example, updating URL parameters, analytics tracking, etc.

      if (swiper) {
        section.dispatchEvent(
          new CustomEvent('whats-in-the-boxes:slide-changed', {
            detail: {
              activeIndex: swiper.activeIndex,
              swiper: swiper,
            },
          })
        );
      }
    }

    // Public method to programmatically switch tabs
    switchToTab(section, tabId) {
      const tab = section.querySelector(`[data-tab="${tabId}"]`);
      if (tab) {
        const tabs = section.querySelectorAll('.wt-whats-in-the-boxes__tab');
        const sliders = section.querySelectorAll('.wt-whats-in-the-boxes__slider');
        this.switchTab(section, tab, tabs, sliders);
      }
    }

    // Public method to get current active tab
    getActiveTab(section) {
      const activeTab = section.querySelector('.wt-whats-in-the-boxes__tab--active');
      return activeTab ? activeTab.dataset.tab : null;
    }
  }

  // Export for potential external use
  window.WhatsInTheBoxes = WhatsInTheBoxes;
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (typeof WhatsInTheBoxes !== 'undefined') {
    new WhatsInTheBoxes();
  }
});

// Re-initialize on theme editor changes
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.wt-whats-in-the-boxes') && typeof WhatsInTheBoxes !== 'undefined') {
      new WhatsInTheBoxes();
    }
  });
}
